import { createAction, props } from '@ngrx/store';
import {
  CreateExerciseDto,
  CreateExerciseSourceDto,
  ExerciseDto,
  UpdateExerciseDto,
} from '@shared/dto';
import { PaginatedListQuery } from '@shared/interfaces/paginated-list-query.interface';

// Read list
export const readExercises = createAction(
  '@app/exercise/read',
  (
    params?: string | number | Record<string, string | number>,
    query?: PaginatedListQuery,
    filters?: Record<string, string | number | boolean>
  ) => ({ params, query, filters })
);
export const readExercisesSuccess = createAction('@app/exercise/read/success', props<{ data: ExerciseDto[]; total: number }>());
export const readExercisesFailure = createAction('@app/exercise/read/failure', props<{ error: any }>());

// Read single (detail) — resolves from the loaded list by id, falling back to a
// server fetch (readOne) so a reload/deep-link always has the full, fresh detail.
export const readExercise = createAction('@app/exercise/read-one', props<{ exerciseId: number }>());
export const readExerciseSuccess = createAction('@app/exercise/read-one/success', props<{ exercise: ExerciseDto }>());
export const readExerciseFailure = createAction('@app/exercise/read-one/failure', props<{ error: any }>());

// Create
export const createExercise = createAction('@app/exercise/create', props<{ dto: CreateExerciseDto }>());
export const createExerciseSuccess = createAction('@app/exercise/create/success', props<{ exercise: ExerciseDto }>());
export const createExerciseFailure = createAction('@app/exercise/create/failure', props<{ error: any }>());

// Update
export const updateExercise = createAction('@app/exercise/update', props<{ exerciseId: number; dto: UpdateExerciseDto }>());
export const updateExerciseSuccess = createAction('@app/exercise/update/success', props<{ exercise: Partial<ExerciseDto> }>());
export const updateExerciseFailure = createAction('@app/exercise/update/failure', props<{ error: any }>());

// Delete
export const deleteExercise = createAction('@app/exercise/delete', props<{ exerciseId: number }>());
export const deleteExerciseSuccess = createAction('@app/exercise/delete/success', props<{ exercise: ExerciseDto }>());
export const deleteExerciseFailure = createAction('@app/exercise/delete/failure', props<{ error: any }>());

// Restore
export const restoreExercise = createAction('@app/exercise/restore', props<{ exerciseId: number }>());
export const restoreExerciseSuccess = createAction('@app/exercise/restore/success', props<{ exercise: ExerciseDto }>());
export const restoreExerciseFailure = createAction('@app/exercise/restore/failure', props<{ error: any }>());

// Sources — managed on their own, independently of the exercise form's save.
//
// The asset section behaves as a media manager: adding or editing a source
// persists it there and then, without touching the rest of the exercise, so an
// unsaved edit to name/duration in the open form is never dragged along.
//
// One row per call. The exercise payload's `sources` is the other way in — the
// complete desired set, reconciled server-side — and it is reserved for the
// create, where there is no exercise id to attach an asset to yet. Everything
// after goes through these, so no edit can drop a row it merely left out.
export const addExerciseSource = createAction(
  '@app/exercise/source/add',
  props<{ exerciseId: number; source: CreateExerciseSourceDto }>(),
);
export const addExerciseSourceSuccess = createAction(
  '@app/exercise/source/add/success',
  props<{ exercise: Partial<ExerciseDto> }>(),
);
export const addExerciseSourceFailure = createAction('@app/exercise/source/add/failure', props<{ error: any }>());

export const updateExerciseSource = createAction(
  '@app/exercise/source/update',
  props<{ exerciseId: number; sourceId: number; source: CreateExerciseSourceDto }>(),
);
export const updateExerciseSourceSuccess = createAction(
  '@app/exercise/source/update/success',
  props<{ exercise: Partial<ExerciseDto> }>(),
);
export const updateExerciseSourceFailure = createAction('@app/exercise/source/update/failure', props<{ error: any }>());

// Deleting hits the dedicated endpoint rather than a reconciling PATCH: it
// removes exactly one row, so a mistake in assembling the array cannot wipe the
// other assets.
export const deleteExerciseSource = createAction(
  '@app/exercise/source/delete',
  props<{ exerciseId: number; sourceId: number }>(),
);
export const deleteExerciseSourceSuccess = createAction(
  '@app/exercise/source/delete/success',
  props<{ exercise: Partial<ExerciseDto> }>(),
);
export const deleteExerciseSourceFailure = createAction('@app/exercise/source/delete/failure', props<{ error: any }>());
